import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plug, Eye, EyeOff, RefreshCw, X } from "lucide-react";
import { getServerSuggestions } from "@/lib/brokerServers";
import TopStepCard from "@/components/TopStepCard";
import { useAuth } from "@/contexts/AuthContext";

export default function Brokers() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [mForm, setMForm] = useState({ login: "", password: "", server: "", broker: "Exness", brokerCustom: "", platform: "mt5", accountType: "demo" });
  const [showMt5Pwd, setShowMt5Pwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reconnectingId, setReconnectingId] = useState(null);

  // Live-derived server suggestions for the datalist
  const serverSuggestions = getServerSuggestions(mForm.broker, mForm.platform, mForm.accountType);

  const load = async () => {
    const { data } = await api.get("/brokers/status");
    setStatus(data);
  };
  const loadProfiles = async () => {
    try {
      const { data } = await api.get("/brokers/mt5/profiles");
      setProfiles(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    loadProfiles();
    const i = setInterval(() => { load(); loadProfiles(); }, 5000);
    return () => clearInterval(i);
  }, []);

  const connectMt5 = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const brokerName = mForm.broker === "Other" ? (mForm.brokerCustom || "Other") : mForm.broker;
      const { data } = await api.post("/brokers/mt5/connect", {
        login: mForm.login, password: mForm.password, server: mForm.server,
        broker: brokerName, platform: mForm.platform,
      });
      toast.success(`MT5 account provisioning · ${data.metaapi_account_id?.slice(0, 8) || "started"}`);
      setMForm({ ...mForm, password: "" });
      await load();
      await loadProfiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally { setBusy(false); }
  };

  const reconnectFromProfile = async (profileId, label) => {
    setReconnectingId(profileId);
    try {
      await api.post(`/brokers/mt5/profiles/${profileId}/connect`);
      toast.success(`Reconnecting to ${label}…`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconnect failed");
    } finally { setReconnectingId(null); }
  };

  const deleteProfile = async (profileId, label) => {
    if (!window.confirm(`Delete saved profile for ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/brokers/mt5/profiles/${profileId}`);
      toast.success(`Profile deleted`);
      await loadProfiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const disconnectMt5 = async () => {
    await api.post("/brokers/mt5/disconnect");
    toast.success("MT5 disconnected (saved profile kept for one-click reconnect)");
    load();
    loadProfiles();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="brokers-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Connections</div>
        <h1 className="text-xl sm:text-2xl font-medium tracking-tight">Brokers</h1>
        <p className="text-sm text-zinc-500 mt-1">Bring your own credentials. We never share them.</p>
      </div>

      <div className="grid lg:grid-cols-1 gap-4">
        {/* MT5 */}
        <div className="border border-white/10 bg-[#121214] rounded-sm" data-testid="mt5-card">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Plug className="w-4 h-4 text-zinc-400" />
              <div className="font-medium">MetaTrader 4 / 5</div>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">via MetaApi</span>
            </div>
            {status?.mt5_connected ? (
              <div className="flex items-center gap-2 text-xs font-mono">
                {status.mt5_bridge_online
                  ? <><span className="dot-online" /><span className="text-teal-400">Connected</span></>
                  : status.mt5_state === "ERROR"
                  ? <><span className="dot-offline" style={{background:'#fb7185'}} /><span className="text-rose-400">Error</span></>
                  : <><span className="dot-warn" /><span className="text-amber-400">{status.mt5_connection_status || status.mt5_state || "Connecting"}</span></>}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono">
                <span className="dot-offline" /> Not configured
              </div>
            )}
          </div>

          {status?.mt5_connected ? (
            <div className="p-4 space-y-3 font-mono text-sm" data-testid="mt5-info">
              <div className="flex justify-between"><span className="text-zinc-500">Broker</span><span>{status.mt5_broker}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Platform</span><span className="uppercase">{status.mt5_platform || "mt5"}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Login</span><span>{status.mt5_login}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Server</span><span>{status.mt5_server}</span></div>
              {status.mt5_balance != null && (
                <div className="flex justify-between"><span className="text-zinc-500">Balance</span><span className="tabular-nums">{status.mt5_balance.toFixed(2)} {status.mt5_currency}</span></div>
              )}
              {status.mt5_equity != null && (
                <div className="flex justify-between"><span className="text-zinc-500">Equity</span><span className="tabular-nums">{status.mt5_equity.toFixed(2)}</span></div>
              )}
              {status.mt5_error && (
                <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 p-2 rounded-sm break-all leading-relaxed" data-testid="mt5-error-banner">
                  <div className="text-rose-400 font-medium mb-1 uppercase tracking-widest text-[10px]">MetaApi Error</div>
                  {status.mt5_error}
                  {status.mt5_error.toLowerCase().includes("top up") && (
                    <div className="mt-2 text-rose-200">
                      → Fix: visit <a className="underline" href="https://app.metaapi.cloud" target="_blank" rel="noreferrer">app.metaapi.cloud</a> → Billing → add at least $10 credit, then remove and re-connect.
                    </div>
                  )}
                  {status.mt5_error.toLowerCase().includes("server") && status.mt5_error.includes("not found") && (
                    <div className="mt-2 text-rose-200">
                      → Fix: open your MT5 terminal, top-left shows the exact server name (e.g. <span className="font-bold">FBS-Real-2</span>, <span className="font-bold">Exness-MT5Trial7</span>). Copy it letter-for-letter.
                    </div>
                  )}
                </div>
              )}
              {!status.mt5_bridge_online && !status.mt5_error && (
                <div className="text-xs text-amber-400 pt-2" data-testid="mt5-handshake-banner">
                  Broker handshake in progress — usually 30–60 seconds. This panel auto-refreshes every 5s.
                </div>
              )}
              {!status.mt5_bridge_online && status.mt5_state === "DEPLOYED" && status.mt5_connection_status === "DISCONNECTED" && (
                <div className="text-xs text-teal-400 pt-1" data-testid="mt5-auto-reconnect-banner">
                  ↻ Auto-reconnect watchdog is active — will re-deploy the bridge automatically if disconnect persists &gt;60s.
                </div>
              )}
              <button onClick={disconnectMt5} data-testid="mt5-disconnect-button"
                      className="mt-2 text-xs uppercase tracking-widest text-rose-400 hover:text-rose-300">
                Remove MT5 credentials <span className="text-zinc-500 normal-case">· saved profile kept</span>
              </button>
            </div>
          ) : (
            <form onSubmit={connectMt5} className="p-4 space-y-3">
              {/* Saved profiles for one-click reconnect */}
              {profiles.length > 0 && (
                <div className="space-y-2" data-testid="mt5-profiles-list">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Saved logins</div>
                  {profiles.map((p) => (
                    <div key={p.id} data-testid={`mt5-profile-${p.id}`}
                         className="border border-white/10 bg-black/40 rounded-sm px-3 py-2 flex items-center gap-2">
                      <div className="flex-1 min-w-0 font-mono text-xs">
                        <div className="text-zinc-100 truncate">{p.broker} · <span className="text-zinc-300">{p.login}</span></div>
                        <div className="text-zinc-500 text-[10px]">{p.platform?.toUpperCase()} · {p.server}</div>
                      </div>
                      <button type="button"
                              data-testid={`mt5-profile-reconnect-${p.id}`}
                              disabled={reconnectingId === p.id}
                              onClick={() => reconnectFromProfile(p.id, `${p.broker} ${p.login}`)}
                              className="text-[10px] uppercase tracking-widest text-teal-400 border border-teal-400/30 hover:bg-teal-400/10 px-2.5 py-1 rounded-sm font-mono disabled:opacity-50">
                        <RefreshCw className={`w-3 h-3 inline ${reconnectingId === p.id ? "animate-spin" : ""}`} />
                        {" "}{reconnectingId === p.id ? "Connecting…" : "Reconnect"}
                      </button>
                      <button type="button"
                              data-testid={`mt5-profile-delete-${p.id}`}
                              onClick={() => deleteProfile(p.id, `${p.broker} ${p.login}`)}
                              className="text-zinc-500 hover:text-rose-400" aria-label="Delete">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <div className="text-[10px] text-zinc-600 pt-1">Or enter new credentials below.</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Broker</label>
                  <select data-testid="mt5-broker-select" value={mForm.broker}
                          onChange={(e) => setMForm({ ...mForm, broker: e.target.value })}
                          className="w-full mt-1 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none">
                    <option>Exness</option>
                    <option>FBS</option>
                    <option>OANDA</option>
                    <option>IC Markets</option>
                    <option>Pepperstone</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Platform</label>
                  <select data-testid="mt5-platform-select" value={mForm.platform}
                          onChange={(e) => setMForm({ ...mForm, platform: e.target.value })}
                          className="w-full mt-1 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none">
                    <option value="mt5">MT5</option>
                    <option value="mt4">MT4</option>
                  </select>
                </div>
              </div>

              {/* Demo / Real radio toggle */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Account Type</label>
                <div className="grid grid-cols-2 gap-2 mt-1" data-testid="mt5-account-type-toggle">
                  <button type="button" data-testid="mt5-account-type-demo"
                          onClick={() => setMForm({ ...mForm, accountType: "demo", server: "" })}
                          className={`px-3 py-2 rounded-sm text-xs font-mono uppercase tracking-widest border transition ${
                            mForm.accountType === "demo"
                              ? "bg-amber-400/10 border-amber-400/50 text-amber-400"
                              : "bg-black border-white/10 text-zinc-500 hover:text-zinc-300"
                          }`}>
                    Demo / Trial
                  </button>
                  <button type="button" data-testid="mt5-account-type-real"
                          onClick={() => setMForm({ ...mForm, accountType: "real", server: "" })}
                          className={`px-3 py-2 rounded-sm text-xs font-mono uppercase tracking-widest border transition ${
                            mForm.accountType === "real"
                              ? "bg-rose-400/10 border-rose-400/50 text-rose-400"
                              : "bg-black border-white/10 text-zinc-500 hover:text-zinc-300"
                          }`}>
                    Real / Live $
                  </button>
                </div>
              </div>
              {mForm.broker === "Other" && (
                <div data-testid="mt5-broker-custom-wrap">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Custom broker name</label>
                  <input data-testid="mt5-broker-custom-input" value={mForm.brokerCustom}
                         onChange={(e) => setMForm({ ...mForm, brokerCustom: e.target.value })}
                         placeholder="e.g. RoboForex, JustMarkets, BlackBull"
                         className="w-full mt-1 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                         required />
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Login (account number)</label>
                <input data-testid="mt5-login-input" value={mForm.login}
                       onChange={(e) => setMForm({ ...mForm, login: e.target.value })}
                       placeholder="12345678"
                       className="w-full mt-1 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                       required />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Password (investor or master)</label>
                <div className="flex gap-1">
                  <input data-testid="mt5-password-input"
                         type={showMt5Pwd ? "text" : "password"}
                         value={mForm.password}
                         onChange={(e) => setMForm({ ...mForm, password: e.target.value })}
                         className="w-full mt-1 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                         required />
                  <button type="button" onClick={() => setShowMt5Pwd(!showMt5Pwd)}
                          className="mt-1 px-2 text-zinc-500 hover:text-zinc-200" tabIndex={-1}>
                    {showMt5Pwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Server</label>
                <input data-testid="mt5-server-input" value={mForm.server}
                       onChange={(e) => setMForm({ ...mForm, server: e.target.value })}
                       placeholder={mForm.accountType === "real" ? "Exness-MT5Real11" : "Exness-MT5Trial7"}
                       list={serverSuggestions.length ? "mt5-server-suggestions" : undefined}
                       className="w-full mt-1 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                       required />
                {serverSuggestions.length > 0 && (
                  <datalist id="mt5-server-suggestions" data-testid="mt5-server-suggestions">
                    {serverSuggestions.map((s) => <option key={s} value={s} />)}
                  </datalist>
                )}
                <div className="text-[10px] text-zinc-600 mt-1">
                  {serverSuggestions.length > 0
                    ? `Click the field to pick from ${serverSuggestions.length} ${mForm.broker} ${mForm.platform.toUpperCase()} ${mForm.accountType} servers, or type your own.`
                    : "Find the exact server name in your broker's MT4/MT5 terminal (top-left near login)."}
                </div>
              </div>
              <button data-testid="mt5-connect-button" disabled={busy}
                      className="w-full bg-zinc-100 text-zinc-900 hover:bg-white py-2 rounded-sm text-sm font-medium disabled:opacity-60">
                {busy ? "Provisioning…" : "Connect MT5 account"}
              </button>
              <div className="text-[10px] text-zinc-600">
                Routed through our MetaApi bridge. You do not need to install anything — the bridge runs on our infrastructure.
              </div>
            </form>
          )}
        </div>

        {/* TopStep / ProjectX Futures — only visible when admin has granted access */}
        {user?.topstep_enabled && <TopStepCard status={status} onChanged={load} />}
      </div>
    </div>
  );
}
